import { useEffect, useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '@/components/Layout';
import { AuthContext } from '@/App';
import axios from 'axios';
import { toast } from 'sonner';
import { Save, Plus, Trash2, Edit, CheckCircle, Clock, Wrench, RefreshCw, Zap, Tag } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function WebmasterPage() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  
  const [notes, setNotes] = useState('');
  const [notesSaved, setNotesSaved] = useState(false);
  const [testingFixes, setTestingFixes] = useState([]);
  const [publishedChanges, setPublishedChanges] = useState([]);
  const [showAddFix, setShowAddFix] = useState(false);
  const [newFix, setNewFix] = useState({ title: '', description: '', status: 'testing' });
  const [syncing, setSyncing] = useState(false);
  const [autoPublishing, setAutoPublishing] = useState(false);

  useEffect(() => {
    if (!user || user.role !== 'admin') {
      navigate('/');
      toast.error('Unauthorized access');
      return;
    }
    loadWebmasterData();
  }, [user, navigate]);

  const loadWebmasterData = async () => {
    try {
      const [notesRes, fixesRes, changesRes] = await Promise.all([
        axios.get(`${API}/admin/webmaster/notes`, { withCredentials: true }),
        axios.get(`${API}/admin/webmaster/testing-fixes`, { withCredentials: true }),
        axios.get(`${API}/admin/webmaster/published-changes`, { withCredentials: true })
      ]);
      
      setNotes(notesRes.data.notes || '');
      setTestingFixes(fixesRes.data);
      setPublishedChanges(changesRes.data);
    } catch (error) {
      console.error('Failed to load webmaster data:', error);
    }
  };

  const handleSaveNotes = async () => {
    try {
      await axios.post(`${API}/admin/webmaster/notes`, { notes }, {
        withCredentials: true
      });
      setNotesSaved(true);
      toast.success('Notes saved!');
      setTimeout(() => setNotesSaved(false), 2000);
    } catch (error) {
      toast.error('Failed to save notes');
    }
  };

  const handleAddFix = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/webmaster/testing-fixes`, newFix, {
        withCredentials: true
      });
      toast.success('Testing fix added!');
      setShowAddFix(false);
      setNewFix({ title: '', description: '', status: 'testing' });
      loadWebmasterData();
    } catch (error) {
      toast.error('Failed to add fix');
    }
  };

  const handleUpdateFixStatus = async (id, status) => {
    try {
      await axios.put(`${API}/admin/webmaster/testing-fixes/${id}`, { status }, {
        withCredentials: true
      });
      toast.success(`Status updated to ${status}!`);
      loadWebmasterData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleDeleteFix = async (id) => {
    if (!window.confirm('Delete this fix?')) return;
    try {
      await axios.delete(`${API}/admin/webmaster/testing-fixes/${id}`, {
        withCredentials: true
      });
      toast.success('Fix deleted');
      loadWebmasterData();
    } catch (error) {
      toast.error('Failed to delete fix');
    }
  };

  const handlePublishFix = async (fix) => {
    if (!window.confirm('Mark this fix as published and move to changelog?')) return;
    try {
      await axios.post(`${API}/admin/webmaster/publish-fix/${fix.id}`, {}, {
        withCredentials: true
      });
      toast.success('Fix published!');
      loadWebmasterData();
    } catch (error) {
      toast.error('Failed to publish fix');
    }
  };

  const handleSyncFromTests = async () => {
    if (!window.confirm('Sync completed tasks from test_result.md? This will auto-add tested features to the testing queue.')) return;
    setSyncing(true);
    try {
      const response = await axios.post(`${API}/admin/webmaster/sync-from-tests`, {}, {
        withCredentials: true
      });
      toast.success(response.data.message);
      loadWebmasterData();
    } catch (error) {
      toast.error('Failed to sync from tests');
    } finally {
      setSyncing(false);
    }
  };

  const handleAutoPublish = async () => {
    if (!window.confirm('Auto-publish all passed fixes to the changelog? This will move all "PASSED" items to Published Changes.')) return;
    setAutoPublishing(true);
    try {
      const response = await axios.post(`${API}/admin/webmaster/auto-publish-passed`, {}, {
        withCredentials: true
      });
      toast.success(response.data.message);
      loadWebmasterData();
    } catch (error) {
      toast.error('Failed to auto-publish');
    } finally {
      setAutoPublishing(false);
    }
  };

  if (!user || user.role !== 'admin') {
    return null;
  }

  const getCategoryBadge = (category) => {
    if (!category) return null;
    const colors = {
      backend: 'bg-blue-500 text-white',
      frontend: 'bg-purple-500 text-white',
      feature: 'bg-green-500 text-white',
      bug: 'bg-red-500 text-white',
      enhancement: 'bg-orange-500 text-white'
    };
    return (
      <span className={`px-2 py-1 rounded text-xs font-bold ${colors[category] || 'bg-gray-500 text-white'}`}>
        {category.toUpperCase()}
      </span>
    );
  };

  return (
    <Layout>
      <section className="py-16 min-h-screen">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-5xl retro-title mb-4 text-center">Webmaster Dashboard</h1>
          <p className="text-center text-[#6B4423] mb-12">Admin-only development and deployment tracking</p>

          {/* Planning Notepad */}
          <div className="cassette-card p-6 mb-8">
            <div className="flex justify-between items-center mb-4">
              <div className="flex items-center gap-3">
                <Edit className="w-6 h-6 text-[#E8934D]" />
                <h2 className="text-3xl retro-title text-[#2C1810]">Planning Notepad</h2>
              </div>
              <button
                onClick={handleSaveNotes}
                className={`retro-button-small flex items-center gap-2 ${notesSaved ? 'bg-green-600' : ''}`}
              >
                {notesSaved ? <CheckCircle className="w-4 h-4" /> : <Save className="w-4 h-4" />}
                {notesSaved ? 'Saved!' : 'Save Notes'}
              </button>
            </div>
            <p className="text-sm text-[#6B4423] mb-4">
              Use this notepad to plan prompts for Emergent coding, track ideas, and organize development tasks.
            </p>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={12}
              placeholder="Plan your prompts and development tasks here..."
              className="w-full px-4 py-3 border-2 border-[#6B4423] rounded bg-[#F4E4C1] text-[#2C1810] font-mono text-sm"
            />
            <p className="text-xs text-[#6B4423] mt-2">
              💡 Tip: Document your planned features, bugs to fix, and prompts before starting development
            </p>
          </div>

          {/* Testing Fixes */}
          <div className="cassette-card p-6 mb-8">
            <div className="flex justify-between items-center mb-4">
              <div className="flex items-center gap-3">
                <Wrench className="w-6 h-6 text-[#E8934D]" />
                <h2 className="text-3xl retro-title text-[#2C1810]">Currently Testing</h2>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={handleSyncFromTests}
                  disabled={syncing}
                  className="retro-button-small flex items-center gap-2 bg-blue-600 text-white"
                  title="Auto-sync from test_result.md"
                >
                  {syncing ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      Syncing...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="w-4 h-4" />
                      Sync from Tests
                    </>
                  )}
                </button>
                <button
                  onClick={handleAutoPublish}
                  disabled={autoPublishing}
                  className="retro-button-small flex items-center gap-2 bg-green-600 text-white"
                  title="Auto-publish all passed fixes"
                >
                  {autoPublishing ? (
                    <>
                      <Zap className="w-4 h-4 animate-pulse" />
                      Publishing...
                    </>
                  ) : (
                    <>
                      <Zap className="w-4 h-4" />
                      Auto-Publish Passed
                    </>
                  )}
                </button>
                <button
                  onClick={() => setShowAddFix(true)}
                  className="retro-button-small flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Add Manually
                </button>
              </div>
            </div>
            <p className="text-sm text-[#6B4423] mb-4">
              Track fixes and features currently being tested. Use "Sync from Tests" to auto-import from test_result.md or add manually.
            </p>

            {testingFixes.length === 0 ? (
              <div className="text-center py-8 text-[#6B4423]">
                <Clock className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No fixes currently being tested</p>
              </div>
            ) : (
              <div className="space-y-3">
                {testingFixes.map((fix) => (
                  <div key={fix.id} className="border-2 border-[#E8B67A] rounded-lg p-4 bg-[#F4E4C1]">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                          <h3 className="text-lg font-bold text-[#2C1810]">{fix.title}</h3>
                          <span className={`px-3 py-1 rounded text-xs font-bold ${
                            fix.status === 'testing' ? 'bg-yellow-500 text-white' :
                            fix.status === 'passed' ? 'bg-green-600 text-white' :
                            'bg-red-500 text-white'
                          }`}>
                            {fix.status.toUpperCase()}
                          </span>
                          {fix.source === 'auto' && (
                            <span className="px-2 py-1 bg-blue-500 text-white rounded text-xs font-bold flex items-center gap-1">
                              <Zap className="w-3 h-3" />
                              AUTO
                            </span>
                          )}
                          {getCategoryBadge(fix.category)}
                          {fix.priority && (
                            <span className={`px-2 py-1 rounded text-xs font-bold ${
                              fix.priority === 'high' ? 'bg-red-100 text-red-800' :
                              fix.priority === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {fix.priority.toUpperCase()}
                            </span>
                          )}
                        </div>
                        {fix.description && (
                          <p className="text-sm text-[#6B4423] mb-2">{fix.description}</p>
                        )}
                        <p className="text-xs text-[#6B4423]">
                          Added: {new Date(fix.created_at).toLocaleDateString()} at {new Date(fix.created_at).toLocaleTimeString()}
                        </p>
                      </div>
                      <div className="flex gap-2 ml-4">
                        {fix.status === 'testing' && (
                          <>
                            <button
                              onClick={() => handleUpdateFixStatus(fix.id, 'passed')}
                              className="p-2 bg-green-100 hover:bg-green-200 rounded"
                              title="Mark as Passed"
                            >
                              <CheckCircle className="w-5 h-5 text-green-700" />
                            </button>
                            <button
                              onClick={() => handleUpdateFixStatus(fix.id, 'failed')}
                              className="p-2 bg-red-100 hover:bg-red-200 rounded"
                              title="Mark as Failed"
                            >
                              <Trash2 className="w-5 h-5 text-red-700" />
                            </button>
                          </>
                        )}
                        {fix.status === 'passed' && (
                          <button
                            onClick={() => handlePublishFix(fix)}
                            className="retro-button-small bg-blue-600 text-white"
                          >
                            Publish to Live
                          </button>
                        )}
                        <button
                          onClick={() => handleDeleteFix(fix.id)}
                          className="p-2 hover:bg-red-200 rounded"
                          title="Delete"
                        >
                          <Trash2 className="w-5 h-5 text-red-600" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Published Changes (Changelog) */}
          <div className="cassette-card p-6">
            <div className="flex items-center gap-3 mb-4">
              <CheckCircle className="w-6 h-6 text-green-600" />
              <h2 className="text-3xl retro-title text-[#2C1810]">Published Changes</h2>
            </div>
            <p className="text-sm text-[#6B4423] mb-4">
              Changelog of all fixes and features deployed to production.
            </p>

            {publishedChanges.length === 0 ? (
              <div className="text-center py-8 text-[#6B4423]">
                <CheckCircle className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No published changes yet</p>
              </div>
            ) : (
              <div className="space-y-3">
                {publishedChanges.map((change) => (
                  <div key={change.id} className="border-l-4 border-green-600 pl-4 py-3 bg-[#F4E4C1] rounded-r">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <h3 className="text-lg font-bold text-[#2C1810]">{change.title}</h3>
                          {getCategoryBadge(change.category)}
                        </div>
                        {change.description && (
                          <p className="text-sm text-[#6B4423] mt-1">{change.description}</p>
                        )}
                        <p className="text-xs text-[#6B4423] mt-2">
                          Published: {new Date(change.published_at).toLocaleDateString()} at {new Date(change.published_at).toLocaleTimeString()}
                        </p>
                      </div>
                      <span className="px-3 py-1 bg-green-600 text-white text-xs font-bold rounded">
                        LIVE
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Add Fix Modal */}
          {showAddFix && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
              <div className="cassette-card p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                <h3 className="text-2xl retro-title text-[#2C1810] mb-4">Add Testing Fix Manually</h3>
                <form onSubmit={handleAddFix} className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold text-[#6B4423] mb-2">Title *</label>
                    <input
                      type="text"
                      value={newFix.title}
                      onChange={(e) => setNewFix({ ...newFix, title: e.target.value })}
                      required
                      placeholder="e.g., Fixed AI generation timeout issue"
                      className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-[#F4E4C1] text-[#2C1810]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-[#6B4423] mb-2">Description</label>
                    <textarea
                      value={newFix.description}
                      onChange={(e) => setNewFix({ ...newFix, description: e.target.value })}
                      rows={3}
                      placeholder="Describe the fix or feature being tested..."
                      className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-[#F4E4C1] text-[#2C1810]"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-[#6B4423] mb-2">Category</label>
                      <select
                        value={newFix.category || ''}
                        onChange={(e) => setNewFix({ ...newFix, category: e.target.value })}
                        className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-[#F4E4C1] text-[#2C1810]"
                      >
                        <option value="">Select category...</option>
                        <option value="backend">Backend</option>
                        <option value="frontend">Frontend</option>
                        <option value="feature">Feature</option>
                        <option value="bug">Bug Fix</option>
                        <option value="enhancement">Enhancement</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-[#6B4423] mb-2">Priority</label>
                      <select
                        value={newFix.priority || ''}
                        onChange={(e) => setNewFix({ ...newFix, priority: e.target.value })}
                        className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-[#F4E4C1] text-[#2C1810]"
                      >
                        <option value="">Select priority...</option>
                        <option value="high">High</option>
                        <option value="medium">Medium</option>
                        <option value="low">Low</option>
                      </select>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <button type="submit" className="retro-button flex-1">
                      Add to Testing
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowAddFix(false);
                        setNewFix({ title: '', description: '', status: 'testing' });
                      }}
                      className="retro-button-small flex-1 bg-gray-400"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>
      </section>
    </Layout>
  );
}
